/**
 * @file Album related helper functions.
 */
import get from 'lodash.get';
import uniqBy from 'lodash.uniqby';
import subDays from 'date-fns/subDays';
import flatten from 'lodash.flatten';

import { isLegacyWeddingEvent, isPpa } from './album';
import { UPLOAD_MODE } from '../constants/album-folder-photos';
import { isHumanJoinCode } from './join-code';
import {
  ALBUM_FOLDER_JOIN_TYPE,
  PHOTO_SHARING_MODE,
} from '../constants/album-folder';
import { canAddGradeTags, canAddUniformTags } from './product-permissions';
import {
  ALBUM_DELIVERY_MODES,
  ALBUM_PRICE_MODE,
  ALBUM_PRIVACY,
  ALBUM_SUBTYPE,
  ALBUM_TYPE,
} from '../constants/albums';
import { getTagsByType } from './album-folder-tags';
import { ALBUM_FOLDER_TAG_TYPE } from '../constants/album-folder-tags';
import { BASENAME } from '../constants/basenames';
import { CONTRIBUTOR_UPLOAD_ACCESS } from '../constants/contributors';
import { getDateWithoutTime } from './date';
import { EVENT_ADDON_ITEM_TYPE } from '../constants/event-items';

const isAlbumFolder = folder => folder?.__typename === 'AlbumFolder';

const folderHasAlbums = folder =>
  folder?.has_album ||
  (folder?.children?.albums?.records ?? []).length > 0 ||
  (folder?.children?.albums?.total ?? 0) > 0;

const folderHasSubFolders = folder =>
  folder.children &&
  folder.children.album_folders &&
  folder.children.album_folders.records &&
  folder.children.album_folders.records.length > 0;

const folderHasChildren = folder => {
  return folderHasAlbums(folder) || folderHasSubFolders(folder);
};

const folderHasSubfoldersByTotals = folder =>
  folder &&
  folder.children &&
  folder.children.album_folders &&
  folder.children.album_folders.total > 0;

const folderHasChildrenByTotals = albumFolder => {
  const hasSubFolders = albumFolder?.children?.album_folders?.total;
  const hasAlbums = albumFolder?.children?.albums?.total;

  return hasSubFolders || hasAlbums;
};

/**
 * Determines if a given album folder is transferable.
 *
 * @param {Object} albumFolder the Album Folder
 * @returns {boolean|*}
 */
const isAlbumFolderTransferable = albumFolder => {
  // @TODO Re-enable this once we support proper hierarchyTransfer changes.
  if (true) {
    return false;
  }

  if (!albumFolder) {
    return false;
  }

  if (typeof albumFolder.is_eligible_for_hierarchy_transfer !== 'undefined') {
    return albumFolder.is_eligible_for_hierarchy_transfer;
  }

  return (
    albumFolder?.identities?.total === 0 &&
    albumFolder?.album_photos?.total === 0
  );
};

const albumFolderIsRoot = folder => folder && !folder.parent;

const folderLabel = folder =>
  albumFolderIsRoot(folder) ? 'gallery' : 'folder';

/**
 * Gets an `album_folder` object returned from MeQuery containing
 * a flat list of album folders with their album children (but on album folder children)
 * and constructs the folder tree.
 *
 * @param {Array} folders - Data as returned from MeQuery (me.album_folders.records).
 * @returns {Array} - Normalized tree of album folders.
 */
const createAlbumFolderTree = folders => {
  // The main idea of this algorithm is we create a lookup table storing
  // every folder we've already visited for quick access.
  const roots = [];
  const lookup = {};

  folders.forEach(folder => {
    const folderId = folder.id;
    const parentId = folder.parent && folder.parent.id;

    // 1. If we don't already have the folder in lookup, add it.
    // Also create the structure necessary to hold its folder children
    if (!lookup[folderId]) {
      lookup[folderId] = {
        ...folder,
        children: {
          album_folders: {
            records: [],
          },
          ...folder.children.albums,
        },
      };
    }

    // 2. At this point we're adding the actual data of this folder to its entry
    // in the lookup table. We spread all of its data because
    // it might be a "dummy" folder we've created in step 3.
    const records = [
      ...get(lookup[folderId], 'children.album_folders.records', []),
      ...get(folder, 'children.album_folders.records', []),
    ];

    lookup[folderId] = {
      ...folder,
      children: {
        ...lookup[folderId].children,
        ...folder.children,
        album_folders: {
          records: uniqBy(records, ({ id }) => id),
        },
      },
    };

    const treeFolder = lookup[folderId];

    if (!parentId) {
      roots.push(treeFolder);
    } else {
      // 3. If it's not a root folder AND we don't already have this folder
      // in lookup, we add a dummy object just to hold its children for now.
      // Later in the iteration, when we actually visit this folder we'll
      // spread all its actual data in step 2, along with the children we add now
      if (!lookup[parentId]) {
        lookup[parentId] = {
          children: {
            album_folders: {
              records: [],
            },
          },
        };
      }
      // 4. Add the child folders to the dummy parent object we created above
      lookup[parentId].children.album_folders.records.push(treeFolder);
    }
  });
  return roots;
};

/**
 * Return album folder child albums (optionally sorted).
 *
 * @param {Object} albumFolder The album folder record.
 * @param {boolean} [sorted] Whether to sort by name.
 * @returns {Object} The album folder's child albums.
 */
const getAlbumFolderChildAlbums = (albumFolder, sorted) => {
  if (!albumFolder) {
    return null;
  }

  const albums = albumFolder?.children?.albums?.records ?? [];

  return sorted
    ? [...albums].sort((a, b) => a.name.localeCompare(b.name))
    : albums;
};

/**
 * Return album folder child album folders (optionally sorted).
 *
 * @param {Object} albumFolder The album folder record.
 * @param {boolean} [sorted] Whether to sort by name.
 * @returns {Object} The album folder's child album folders.
 */
const getAlbumFolderChildAlbumFolders = (albumFolder, sorted) => {
  if (!albumFolder) {
    return null;
  }

  const albumFolders = albumFolder?.children?.album_folders?.records ?? [];

  return sorted
    ? [...albumFolders].sort((a, b) => {
        if (a.display_order !== null && b.display_order === null) {
          return -1;
        }

        if (a.display_order === null && b.display_order !== null) {
          return 1;
        }

        if (a.display_order < b.display_order) {
          return -1;
        }

        if (a.display_order > b.display_order) {
          return 1;
        }

        return a.name.localeCompare(b.name);
      })
    : albumFolders;
};

/**
 * Return album folder children (optionally sorted album folders and albums).
 *
 * @param {Object} albumFolder The album folder record.
 * @param {boolean} [sorted] Whether to sort by name.
 * @returns {Object} The album folder children data.
 */
const getAlbumFolderChildren = (albumFolder, sorted) => {
  if (!albumFolder) {
    return null;
  }

  return {
    albumFolders: getAlbumFolderChildAlbumFolders(albumFolder, sorted),
    albums: getAlbumFolderChildAlbums(albumFolder, sorted),
  };
};

/**
 * Returns the flattened albums for a given albumFolder up to level 3 (query's default).
 *
 * @param {Object} albumFolder the album fodler record
 * @param {boolean} sorted whether to sort by name
 * @returns {Array} albums
 */
const getAlbumFolderAlbumFlatList = (albumFolder, sorted) => {
  const albums = [];
  const children = getAlbumFolderChildren(albumFolder);

  // Level 1
  if ((children?.albumFolders ?? []).length > 0) {
    children.albumFolders.forEach(subFolder => {
      // Level 2
      const subChildren = getAlbumFolderChildren(subFolder);
      if ((subChildren?.albumFolders ?? []).length > 0) {
        subChildren.albumFolders.forEach(subSubFolder => {
          // Level 3
          const subSubChildren = getAlbumFolderChildren(subSubFolder);
          if ((subSubChildren.albums ?? []).length > 0) {
            albums.push(subSubChildren.albums);
          }
        });
      } else if ((subChildren?.albums ?? []).length > 0) {
        albums.push(subChildren.albums);
      }
    });
  } else if ((children?.albums ?? []).length > 0) {
    albums.push(children.albums);
  }

  if (sorted) {
    return flatten(albums).sort((a, b) => a.name.localeCompare(b.name));
  }

  return flatten(albums);
};

/**
 * Traverses a flat list of connected folders with a parent/child
 * relationship given starting folder ID and returns the first ancestor
 * folder that has a price.
 *
 * @param {String} folderId The folder to start from.
 * @param {Object} folders Flat list of folders as returned via GQL.
 * @returns {object|undefined} The subscription folder or undefined if nothing is found.
 */
const getAlbumFolderSubscription = (folderId, folders) => {
  if (!folderId || !folders) {
    return undefined;
  }

  const folder = folders.find(({ id }) => folderId === id);

  if (
    !folder ||
    (folder.price && folder.price.value) ||
    folder.search_subscription_credits_joins_enabled
  ) {
    return folder;
  }

  if (!folder.parent) {
    return undefined;
  }

  return getAlbumFolderSubscription(folder.parent.id, folders);
};

/**
 * Accepts a root folder with its full tree hierarchy (down to all albums)
 * and determines if any of its albums have a PPA pricing attached.
 *
 * @param {Object} folder The folder to check.
 * @returns {boolean} Whether any of the folder's albums have a PPA price attached on them.
 */
const folderHasPpaAlbums = folder => {
  if (!folder) {
    throw Error('No folder provided');
  }

  if (!folderHasChildren(folder)) {
    return false;
  }

  if (folder && folder.children.albums.records.some(isPpa)) {
    return true;
  }

  return folder.children.album_folders.records.some(subFolder =>
    folderHasPpaAlbums(subFolder),
  );
};

/**
 * Traverses a flat list of folders with a parent/child
 * relationship given starting folder ID and returns an array
 * of all the ancestors' IDs of the provided folder (plus the folder ID itself).
 *
 * @param {String} folderId The folder to start from.
 * @param {Object} folders Flat list of folders as returned via GQL.
 * @param {Array} hierarchy Internal array storing the IDs, not necessary to be provided.
 * @returns {Array} - An array of the folder ID plus its ancestors' IDs.
 */
const getFolderAncestorHierarchy = (folderId, folders, hierarchy = []) => {
  if (!folderId || !folders) {
    return undefined;
  }

  const folder = folders.find(({ id }) => folderId === id);

  if (!folder) {
    return hierarchy;
  }

  hierarchy.push(folder.id);

  if (!folder.parent) {
    return hierarchy;
  }

  return getFolderAncestorHierarchy(folder.parent.id, folders, hierarchy);
};

/**
 * Returns whether or not an albumFolder contains a
 * least a single cover photo.
 *
 * @param {Object} albumFolder The album folder record.
 * @returns {boolean} Whether the albumFolder has cover photos or not.
 */
const albumFolderHasCoverPhotos = albumFolder => {
  return (
    albumFolder.cover_photos &&
    albumFolder.cover_photos.records &&
    albumFolder.cover_photos.records.length > 0
  );
};

/**
 * Returns whether or not an albumFolder contains
 * a charity (i.e. Part of the proceedings will be given away).
 *
 * @param {Object} albumFolder The album folder record.
 * @returns {boolean} Whether the albumFolder has a charity or not.
 */
const albumFolderHasCharity = albumFolder =>
  albumFolder && albumFolder.has_charity;

/**
 * Returns whether or not an albumFolder is prepaid.
 *
 * @param {Object} albumFolder The album folder record.
 * @returns {boolean} Whether the album folder is prepaid or not.
 */
const albumFolderIsPrePaid = albumFolder => {
  return !!albumFolder?.is_demo || !!albumFolder?.pre_paid;
};

/**
 * Returns whether or not an albumFolder has a free trial album.
 *
 * @param {Object} albumFolder The album folder record.
 * @returns {boolean} Whether the album folder has a free trial album.
 */
const albumFolderHasFreeTrial = albumFolder => {
  return albumFolder && albumFolder.has_free_trial;
};

/**
 * Returns whether or not a folder is watermarked.
 *
 * @param {Object} folder The folder object.
 * @returns {boolean|undefined} Whether the folder has watermark.
 */
const isFolderWatermarked = folder => {
  if (!folder) {
    return undefined;
  }

  return !!(folder.watermark_config && folder.watermark_config.id);
};

const getUploadUrl = (basename, albumFolder) => {
  return albumFolder.upload_mode === UPLOAD_MODE.REVIEW
    ? `/app/${basename}/folders/${albumFolder.id}/upload-review`
    : `/app/${basename}/folders/${albumFolder.id}/upload-publish`;
};

/**
 * Returns whether an album folder can be discovered or not.
 * For an album folder to be discovered it needs to have a human join code.
 *
 * @param {Object} albumFolder The album folder.
 * @returns {boolean} Whether the album folder can be discovered or not.
 */
const isAlbumFolderDiscoverable = albumFolder => {
  if (!albumFolder) {
    return false;
  }

  return isHumanJoinCode(albumFolder.join_code);
};

/**
 * Given the tree structure of an album folder,
 * will transverse to get the path to follow
 * in order to find folder ID.
 *
 * @param {Object} childFolder Folder we're looking for.
 * @param {Object} parentFolder Root of the tree structure the child belongs to.
 * @returns {Array} - An array of the folder IDs starting from the highest ancestor
 * down to folderId.
 */
const getFolderTreeHierarchy = (childFolder, parentFolder) => {
  if (!childFolder || !parentFolder) {
    return undefined;
  }

  if (childFolder.id === parentFolder.id) {
    // Base case
    return [childFolder];
  }

  const albumFolders = get(parentFolder, 'children.album_folders.records', []);

  for (let i = 0; i < albumFolders.length; i += 1) {
    const childrenFromParent = albumFolders[i];
    const childFolderPath = getFolderTreeHierarchy(
      childFolder,
      childrenFromParent,
    );
    if (childFolderPath) {
      return [parentFolder, ...childFolderPath];
    }
  }

  return undefined;
};

/**
 * Given an organized array of Album Folders representing the hierarchy,
 * returns a concatenated string of the Album folder names.
 *
 * @param {Array<AlbumFolder>} hierarchy The organized array of Album Folders.
 */
const getAlbumFolderHierarchyString = hierarchy => {
  if (!hierarchy) {
    return '';
  }

  return hierarchy
    .map((albumFolder, index) =>
      index === hierarchy.length - 1
        ? albumFolder.name
        : `${albumFolder.name} - `,
    )
    .join('');
};

/**
 * Given an album folder, determines if the flow to follow
 * is the folder subscription required one.
 *
 * @param {AlbumFolder} albumFolder - The album folder object.
 * @param {ALBUM_FOLDER_JOIN_TYPE} albumFolder.join_type - The join type of the album folder.
 * @param {boolean} albumFolder.requires_album_folder_onboarding - The join type of the album folder.
 */
const albumFolderSubscriptionRequired = albumFolder => {
  const isJoinTypeAllAlbums =
    get(albumFolder, 'join_type') === ALBUM_FOLDER_JOIN_TYPE.ALL;
  const requiresAlbumFolderOnboarding = get(
    albumFolder,
    'requires_album_folder_onboarding',
  );

  return (
    isJoinTypeAllAlbums ||
    requiresAlbumFolderOnboarding ||
    (albumFolder?.individual_albums &&
      albumFolder?.album_type === ALBUM_TYPE.SHUTTERBUG)
  );
};

/**
 * Returns a flattened & filtered list cleaning out all identities.
 * Will add empty object to matching photos if none is found.
 *
 * @param {object} identities List of identities from an albumFolder.
 * @returns {Array} Identities.
 */
const getAlbumFolderIdentities = (identities = []) => {
  if (!identities) {
    return [];
  }

  // Flatten the selected identities into a single array
  const filteredRows = identities.map(row =>
    row.current_matching_photo
      ? row
      : {
          ...row,
          current_matching_photo: {},
        },
  );

  // Return only unique identities.
  return uniqBy(filteredRows, ({ id }) => id);
};

/**
 * Returns the correct link screen for album folder tags depending on
 * the album folder's subtype.
 *
 * @param {BASENAME} basename - The basename.
 * @param {Object} albumFolder - The album folder.
 * @returns {string} - The link to the album folder's tag settings view.
 */
const getAlbumFolderTagLinkBySubtype = (basename, albumFolder) => {
  if (!basename || !albumFolder) {
    return '';
  }

  if (canAddGradeTags(albumFolder) || canAddUniformTags(albumFolder)) {
    return `/app/${basename}/folders/${albumFolder.id}/tags/smart`;
  }

  return `/app/${basename}/folders/${albumFolder.id}/tags/custom`;
};

/**
 * Determines whether tag selection is required before performing
 * checkout.
 *
 * @param {AlbumFolder} albumFolder The Album Folder object.
 */
const isAlbumFolderTagSelectionRequiredForCheckout = albumFolder => {
  // TODO: Should all be handled by a proper albumFolder config.
  // This is a temporary measure to test feasibility. #youthsports
  return get(albumFolder, 'album_subtype') === ALBUM_SUBTYPE.YOUTH_SPORTS;
};

/**
 * Determines if users should be able to select activitites based on the albumFolderTags list.
 *
 * @param {Array<AlbumFolderTag>} tags List of albumFolderTags.
 */
const canSelectActivitities = tags => {
  return getTagsByType(ALBUM_FOLDER_TAG_TYPE.UNIFORM, tags).length > 0;
};

/**
 * Determines whether the onboarding flow for the particular AlbumFolder
 * should request a gender from onboarding identities
 * when they are being created or updated.
 *
 * @returns {boolean}
 */
const requiresGenderForOnboarding = () => {
  return false;
};

/**
 * Returns whether an AlbumFolder should be onboarded via photo searches instead
 * of normal check out.
 *
 * @param {Object} albumFolder - The AlbumFolder.
 * @returns {boolean}
 */
const albumFolderRequiresPhotoSearchOnboarding = albumFolder => {
  if (!albumFolder) {
    return false;
  }

  return (
    albumFolder.album_type === ALBUM_TYPE.SHUTTERBUG || // Shutterbug default.
    albumFolder.search_subscription_credits_joins_enabled || // AlbumFolder level config
    (albumFolder?.album_delivery_modes?.shutterbug && // Album level config
      !albumFolder?.album_delivery_modes?.photo_finder)
  );
};

/**
 * Returns a list of identities based off of a Roster Identity list.
 *
 * @param {Array<RosterIdentity>} rosterIdentities The list of roster identities to make as identities.
 */
const getAlbumFolderIdentitiesFromRosterIdentities = (
  rosterIdentities = [],
) => {
  if (!rosterIdentities || rosterIdentities.length === 0) {
    return [];
  }

  const rostedIdentitiesAsIdentities = rosterIdentities.map(rosterIdentity => {
    const identity = get(
      rosterIdentity,
      'linked_identities.records[0].identity',
    );

    if (!identity) {
      return {
        ...rosterIdentity,
        name: rosterIdentity.first_name,
        current_matching_photo: rosterIdentity.selfie_photo,
      };
    }

    return {
      ...identity,
      ...rosterIdentity,
      identityId: identity.id,
      name: rosterIdentity.first_name,
      current_matching_photo:
        rosterIdentity.selfie_photo || identity.current_matching_photo,
    };
  });

  return getAlbumFolderIdentities(rostedIdentitiesAsIdentities);
};

/**
 * Returns the Album Folder's payment descriptor.
 *
 * @param {Object} albumFolder The AlbumFolder.
 * @returns {string} The payment descriptor, i.e PPA, PPG, PPG/D, PPG Drip, etc.
 */
const getAlbumFolderPaymentDescriptor = albumFolder => {
  if (!albumFolder) {
    return 'N/A';
  }

  const hasPPG = !!albumFolder.price?.value;
  const isPPGOnly =
    hasPPG && albumFolder.album_price_mode === ALBUM_PRICE_MODE.NONE;
  const isPPA = albumFolder.album_price_mode === ALBUM_PRICE_MODE.PPA_ONLY;
  const isPPD = albumFolder.album_price_mode === ALBUM_PRICE_MODE.PPD_ONLY;
  const isPPGD = hasPPG && isPPD;
  const isPPAD = albumFolder.album_price_mode === ALBUM_PRICE_MODE.PPA_AND_PPD;
  const isPPDOnly = isPPD && !hasPPG;

  const isDrip = albumFolder.join_without_purchase;

  const label = (() => {
    if (isPPGOnly) {
      return 'PPG';
    }

    if (isPPA) {
      return 'PPA';
    }

    if (isPPGD) {
      return 'PPG/D';
    }

    if (isPPAD) {
      return 'PPA/D';
    }

    if (isPPDOnly) {
      return 'PPD';
    }

    return 'N/A';
  })();

  return `${label}${isDrip ? ' Drip' : ''}`;
};

/**
 * Returns an AlbumFolder's discount price desciptor.
 *
 * @param {Object} albumFolder The AlbumFolder.
 * @returns {string}
 */
const getAlbumFolderDiscountDescriptor = albumFolder => {
  if (!albumFolder) {
    return '';
  }

  if (albumFolder.limited_time_offer_active) {
    return 'Limited Time Offer';
  }

  if (albumFolder.day_pricings_enabled) {
    return 'Day Pricings';
  }

  return '';
};

const getAlbumFolderPhotoDeliveryDescriptor = albumFolder => {
  if (albumFolder.photo_finder_credits) {
    return 'Deliver to Everyone';
  }
  if (albumFolder.shutterbug_delivery) {
    return 'Deliver to Waldo Subscribers';
  }
  if (albumFolder.parent_pay) {
    return 'Parent Pay';
  }
  if (albumFolder.free_web_gallery_only) {
    return 'Free Web Gallery';
  }
  if (albumFolder.selected_albums_only) {
    return 'Selected Albums Only';
  }

  return 'Internal Use';
};

/**
 * Determines whether tag selection allows for partial tag completition or not.
 *
 * @param {AlbumFolder} albumFolder The Album Folder object.
 */
const allowsPartialSmartTagCompletion = albumFolder => {
  return albumFolder?.album_subtype === ALBUM_SUBTYPE.SCHOOLS;
};

/**
 * Gets albumFolder's assigned print packages by cross-referencing
 * their IDs to the account's print packages.
 *
 * @param {Array} printPackages - The account's print packages.
 * @param {Array} albumFolderPrintPackages - Album Folder' print packages.
 * @returns {Array} - The albumFolder's assigned print package details.
 */
const getAlbumFolderAssignedPrintPackages = (
  printPackages,
  albumFolderPrintPackages,
) => {
  if (!printPackages.length || !albumFolderPrintPackages.length) {
    return [];
  }

  return printPackages.filter(printPackage =>
    albumFolderPrintPackages.find(
      albumFolderPrintPackage =>
        albumFolderPrintPackage.print_package.id === printPackage.id,
    ),
  );
};

/**
 * Gets albumFolder's un-assigned print packages by cross-referencing
 * their IDs to the account's print packages.
 *
 * @param {Array} printPackages - The account's print packages.
 * @param {Array} albumFolderPrintPackageIds - AlbumFolder's print package Ids.
 * @returns {Array} - The albumFolder's un-assigned print package details.
 */
const getAlbumFolderUnassignedPrintPackages = (
  printPackages,
  albumFolderPrintPackageIds = [],
) => {
  if (!printPackages.length) {
    return [];
  }

  return printPackages.filter(
    printPackage => !albumFolderPrintPackageIds.includes(printPackage.id),
  );
};

/**
 * Returns whether or  not an album folder is Photo Finder Credits Delivery Enabled.
 *
 * @param {Object} albumFolder The album folder to check.
 */
const isPhotoFinderCreditsDeliveryEnabled = albumFolder =>
  (albumFolder?.is_demo && albumFolder?.org_pay_photo_pricing_enabled) ||
  albumFolder?.default_album_delivery_mode ===
    ALBUM_DELIVERY_MODES.PHOTO_FINDER;

/**
 * Returns whether or  not an album folder is Photo Finder Credits + PPG Delivery Enabled.
 *
 * @param {Object} albumFolder The album folder to check.
 */
const isPhotoFinderCreditsPPGDeliveryEnabled = albumFolder =>
  [
    PHOTO_SHARING_MODE.PHOTO_FINDER,
    PHOTO_SHARING_MODE.WEB_GALLERY_AND_PHOTO_FINDER,
  ].includes(albumFolder?.photo_sharing_mode) &&
  !albumFolder?.is_demo &&
  albumFolder?.price?.value > 0;

/**
 * Returns whether or  not an album folder is Shutterbug delivery enabled.
 *
 * @param {Object} albumFolder The album folder to check.
 */
const isShutterbugDeliveryEnabled = albumFolder =>
  albumFolder?.search_subscription_credits_joins_enabled ||
  albumFolder?.default_album_delivery_mode === ALBUM_DELIVERY_MODES.SHUTTERBUG;

/**
 * Returns whether or  not an album folder is parent pay delivery enabled.
 *
 * @param {Object} albumFolder The album folder to check.
 */
const isParentPayDeliveryEnabled = albumFolder =>
  (albumFolder?.price?.value === 1 &&
    albumFolder.privacy === ALBUM_PRIVACY.PRIVATE) ||
  albumFolder?.default_album_delivery_mode === ALBUM_DELIVERY_MODES.PPA ||
  albumFolder?.album_price_mode === ALBUM_PRICE_MODE.PPA_ONLY;

/**
 * Returns whether or  not an album folder has delivery setup.
 *
 * @param {Object} albumFolder The album folder to check.
 */
const isPhotoDeliverySetup = albumFolder =>
  albumFolder?.picture_day_enabled ||
  isPhotoFinderCreditsDeliveryEnabled(albumFolder) ||
  isPhotoFinderCreditsPPGDeliveryEnabled(albumFolder) ||
  isShutterbugDeliveryEnabled(albumFolder) ||
  isParentPayDeliveryEnabled(albumFolder) ||
  // Admin configured is_demo / pre_paid
  albumFolder?.is_demo ||
  albumFolder?.pre_paid;

/**
 * Determines if the album folder has pending link requests or not.
 * At the moment, `albumFolder.link_requests` is only included in the album
 * folder for situations the target-album-folder is awaiting for response to be
 * linked.
 *
 * @param {Object} albumFolder The album folder to check.
 * @returns {boolean}
 */
const albumFolderHasPendingLink = albumFolder =>
  albumFolder?.link_requests?.total > 0 ||
  (albumFolder?.link_requests?.records ?? []).length > 0;

/**
 * Determines if the album folder is destined for internal usage only.
 *
 * @param {Object} albumFolder The album folder to check.
 */
const isAlbumFolderForInternalUse = albumFolder =>
  albumFolder?.photo_sharing_mode === PHOTO_SHARING_MODE.INTERNAL;

/**
 * Uses the photo sharing mode to determine whether an album folder should have access to matching
 * settings & delivery options.
 *
 * @param {Object} albumFolder The album folder to check.
 */
const isAlbumFolderPaidMatching = albumFolder =>
  [
    PHOTO_SHARING_MODE.WEB_GALLERY_AND_PHOTO_FINDER,
    PHOTO_SHARING_MODE.PHOTO_FINDER,
  ].includes(albumFolder?.photo_sharing_mode);

/**
 * Determines if the album folder is destined for web gallery usage only.
 *
 * @param {Object} albumFolder The album folder to check.
 */
const isAlbumFolderForWebGalleryOnly = albumFolder =>
  albumFolder?.photo_sharing_mode === PHOTO_SHARING_MODE.WEB_GALLERY ||
  (!isPhotoDeliverySetup(albumFolder) &&
    albumFolder?.photo_sharing_mode ===
      PHOTO_SHARING_MODE.WEB_GALLERY_AND_PHOTO_FINDER);

/**
 * Determines if the album folder has a vote on whether FWG is enabled or not.
 *
 * @param {Object} albumFolder The album folder to check.
 */
const albumFolderCanSelectFreeWebGalleryOnly = albumFolder =>
  [
    PHOTO_SHARING_MODE.WEB_GALLERY_AND_PHOTO_FINDER,
    PHOTO_SHARING_MODE.WEB_GALLERY,
  ].includes(albumFolder?.photo_sharing_mode);

/**
 * Returns the base url for all tag-related configuration links based on the album fodler config (Share vs Sell photos)
 * and the currnet basename (Shutterbug vs everythingelse).
 *
 * @param {String} basename Current basename.
 * @param {Object} albumFolder The album folder to get tag links for.
 * @returns {String}
 */
const getAlbumFolderTagBaseUrlByBasename = (basename, albumFolder) => {
  if (basename === BASENAME.SHUTTERBUG) {
    return `/app/${basename}/folders/${albumFolder.id}/tags`;
  }

  return albumFolder.picture_day_enabled
    ? `/app/${basename}/picture-day/folders/${albumFolder.id}/settings/tags`
    : `/app/${basename}/folders/${albumFolder.id}/settings/tags`;
};

/**
 * Determines whether an album folder / album is onboardable based on
 * the photo sharing mode settings.
 *
 * @param {Object} albumFolder
 */
const albumFolderIsOnboardable = albumFolder => {
  if (!albumFolder) {
    return false;
  }

  if (
    // Check is not undefined to avoid wrong interactions
    // With an album folder that might be joinable.
    typeof albumFolder?.joins_enabled !== 'undefined' &&
    !albumFolder?.joins_enabled
  ) {
    return false;
  }

  return (
    // Tecnically, joins_enabled rules all as of 2021.
    albumFolder.joins_enabled ||
    [
      PHOTO_SHARING_MODE.PHOTO_FINDER,
      PHOTO_SHARING_MODE.WEB_GALLERY_AND_PHOTO_FINDER,
    ].includes(albumFolder.photo_sharing_mode) ||
    // Fallbacks. Sharing mode is unsafe if Ops is the one
    // configuring things.
    albumFolder.is_demo ||
    albumFolder.album_delivery_modes?.shutterbug ||
    albumFolder.album_delivery_modes?.ppa ||
    albumFolder.album_delivery_modes?.photo_finder ||
    (albumFolder.album_price_mode &&
      albumFolder.album_price_mode !== ALBUM_PRICE_MODE.NONE)
  );
};

/**
 * Returns whether an AlbumFolder should display a Search Subscription CTA after checkout or not.
 *
 * @param {Object} albumFolder - The AlbumFolder.
 * @returns {boolean}
 */
const albumFolderHasSearchSubscriptionContent = albumFolder => {
  if (!albumFolder) {
    return false;
  }

  return albumFolder?.album_delivery_modes?.shutterbug;
};

/**
 * Returns whether an AlbumFolder should display a Search Subscription CTA after checkout or not.
 *
 * @param {Object} albumFolder - The AlbumFolder.
 * @returns {boolean}
 */
const albumFolderHasPhotoFinderCreditsContent = albumFolder => {
  if (!albumFolder) {
    return false;
  }

  return albumFolder?.album_delivery_modes?.photo_finder;
};

/**
 * Returns whether an Album Folder has photo finder (SB, SB+PF, PF) enabled content.
 *
 * @param {Object} albumFolder - The AlbumFolder.
 * @returns {boolean}
 */
const isAlbumFolderPhotoFinderEnabled = albumFolder => {
  if (!albumFolder) {
    return false;
  }

  return (
    albumFolderHasPhotoFinderCreditsContent(albumFolder) ||
    albumFolderHasSearchSubscriptionContent(albumFolder)
  );
};

/**
 * Returns whether an Album Folder allows anyone to upload or not
 *
 * @param {Object} albumFolder - The AlbumFolder.
 * @returns {boolean}
 */
const albumFolderAnyoneCanUploadEnabled = albumFolder => {
  if (!albumFolder) {
    return false;
  }

  if (
    albumFolder.auto_contributor_settings?.upload_access &&
    albumFolder.auto_contributor_settings?.upload_access !==
      CONTRIBUTOR_UPLOAD_ACCESS.NONE
  ) {
    return true;
  }

  return false;
};

/**
 * Returns whether an Album Folder has access to smart tags
 *
 * @param {Object} albumFolder - The AlbumFolder.
 * @returns {boolean}
 */
const albumFolderHasSmartTagsAccess = albumFolder => {
  if (!albumFolder) {
    return false;
  }

  const sharingModesWithSmartTags = [
    PHOTO_SHARING_MODE.PHOTO_FINDER,
    PHOTO_SHARING_MODE.WEB_GALLERY_AND_PHOTO_FINDER,
  ];

  return (
    albumFolder.picture_day_enabled ||
    sharingModesWithSmartTags.includes(albumFolder.photo_sharing_mode)
  );
};

/**
 * Returns whether an Album Folder has access to revenue tab or not.
 * If you update this method - be sure to update canAccessRevenueDashboard accordingly,
 *
 * TODO: At some point (i.e, after camps) we will merge this method into canAccessRevenueDashboard.
 *
 * @param {Object} albumFolder - The AlbumFolder.
 * @returns {boolean}
 */
const canAccessRevenueTab = albumFolder => {
  if (!albumFolder) {
    return false;
  }

  const isRootAlbumFolder = !albumFolder?.parent;

  if (!isRootAlbumFolder) {
    return false;
  }

  if (albumFolder.is_demo) {
    return false;
  }

  if (albumFolder.search_subscription_credits_joins_enabled) {
    return false;
  }

  if (albumFolder.picture_day_enabled) {
    return true;
  }

  if (
    albumFolder.album_custom_prints?.total > 0 &&
    ![ALBUM_SUBTYPE.CAMPS].includes(albumFolder.album_subtype)
  ) {
    return true;
  }

  // Show the Revenue tab on "Share" galleries that have FWG pricing on.
  if (
    albumFolder.photo_sharing_mode &&
    albumFolder.is_web_gallery &&
    albumFolder.web_gallery_price_per_photo?.value > 0
  ) {
    return true;
  }

  return false;
};

/**
 * Determines whether or not the PPA album creation warning should be displayed on an album folder.
 *
 * @param {Object} albumFolder The Album folder to check.
 * @returns {boolean}
 */
const shouldDisplayPPAWarning = albumFolder => {
  if (
    albumFolder.album_subtype === ALBUM_SUBTYPE.CAMPS &&
    albumFolder.album_price_mode === ALBUM_PRICE_MODE.PPA_ONLY
  ) {
    return true;
  }

  return false;
};

/**
 * Determines wether or not a given gallery is outdated (has not been updated in the last 90 days)
 *
 * @param {Object} albumFolder the Album folder to check
 * @returns {boolean}
 */
const isOutdatedAlbumFolder = albumFolder => {
  const threshold = subDays(getDateWithoutTime(), 90);

  const endDate = albumFolder.end_date ? new Date(albumFolder.end_date) : null;
  const updatedAt = albumFolder.updated_at
    ? new Date(albumFolder.updated_at)
    : null;

  if (endDate && endDate > threshold) {
    return false;
  }

  if (updatedAt && updatedAt > threshold) {
    return false;
  }

  return !!albumFolder.is_outdated;
};

const albumFolderHasPurchasableAddons = albumFolder => {
  if (albumFolder?.album_type !== ALBUM_TYPE.EVENT) {
    return false;
  }

  if (isLegacyWeddingEvent(albumFolder)) {
    return false;
  }

  if (
    albumFolder?.purchased_event_addon_types?.includes(
      EVENT_ADDON_ITEM_TYPE.WALDOFIED_PHOTO_FINDER,
    ) ||
    albumFolder?.purchased_event_addon_types?.includes(
      EVENT_ADDON_ITEM_TYPE.WALDOFIED_PHOTO_FINDER_PLUS,
    )
  ) {
    // Avoids PRO being able to upgrade
    return false;
  }

  // TODO: Remove once Group Video makes it in. At said point, all should rely on < 3
  if (
    [ALBUM_SUBTYPE.MEMORIALS, ALBUM_SUBTYPE.WEDDINGS].includes(
      albumFolder?.album_subtype,
    )
  ) {
    return albumFolder?.purchased_event_addon_types?.length < 3;
  }

  // Currently we don't support 3 outside of VGB, so go with 2.
  return albumFolder?.purchased_event_addon_types?.length < 2;
};

/**
 * Determines whether or not we should show the taxes setting on a given albumFolder
 *
 * @param {Object} albumFolder the album folder
 * @returns {Boolean}
 */
const folderShouldShowTaxesSettings = albumFolder => {
  if (albumFolder?.price?.value) {
    return true;
  }

  if (
    albumFolder?.is_web_gallery &&
    (albumFolder?.web_gallery_print_purchases_enabled ||
      albumFolder.web_gallery_price_per_photo?.value > 0)
  ) {
    return true;
  }

  if (albumFolder?.custom_prints?.records?.length > 0) {
    return true;
  }

  return false;
};

/**
 * Determines if an album folder should be joined/subscribed to with
 * multiple faces without much on user's confirmation and interaction.
 *
 * This album-folder should pretty much mimic Native's join experience.d
 *
 * @param {AlbumFolder} albumFolder - The album folder object.
 */
const albumFolderSupportsSimplifiedJoin = albumFolder => {
  const supportedAlbumTypes = [ALBUM_TYPE.EVENT];
  const isJoinTypeAllAlbums =
    albumFolder?.join_type === ALBUM_FOLDER_JOIN_TYPE.ALL;

  return (
    !albumFolder?.price?.value &&
    supportedAlbumTypes.includes(albumFolder?.album_type) &&
    (isJoinTypeAllAlbums || albumFolder.individual_albums)
  );
};

/**
 * Determines if a given albumFolder is the athletics folder.
 *
 * @param {Object} albumFolder the albumFolder
 * @returns {boolean}
 */
const isAthleticsAlbumFolder = albumFolder => {
  if (!albumFolder) {
    return false;
  }
  if (!isAlbumFolder(albumFolder)) {
    return false;
  }

  if (albumFolderIsRoot(albumFolder)) {
    return false;
  }

  if (albumFolder?.name !== 'Athletics') {
    return false;
  }

  const uniformTag = (albumFolder?.root?.uniform_tags?.records ?? [])?.[0];

  if (!uniformTag?.uniform_enabled || !uniformTag?.uniform_numbered) {
    return false;
  }

  return true;
};

/**
 * Determines if a given albumFolder is uniform-enabled with NJR
 *
 * @param {Object} albumFolder the albumFolder
 * @returns {boolean}
 */
const isNumberedJerseysAlbumFolder = albumFolder => {
  if (!albumFolder) {
    return false;
  }

  const uniformTag = (albumFolder?.root?.uniform_tags?.records ?? [])?.[0];

  if (!uniformTag?.uniform_enabled || !uniformTag?.uniform_numbered) {
    return false;
  }

  return true;
};

/**
 * Determines if a given albumFolder has prepaid items
 *
 * @param {Object} albumFolder the albumFolder
 * @returns {boolean}
 */
const isAlbumFolderWithPrepaidItems = albumFolder => {
  if (!albumFolder) {
    return false;
  }

  return (
    albumFolder?.prepaid_digital_bundles_enabled ||
    albumFolder?.prepaid_print_packages_enabled ||
    albumFolder?.prepaid_photo_pricings_enabled ||
    albumFolder?.prepaid_custom_prints_enabled
  );
};

const isPrepaidOnlyAlbumFolder = albumFolder => {
  if (!albumFolder) {
    return false;
  }

  if (!albumFolder?.join_requires_purchase) {
    return false;
  }

  return isAlbumFolderWithPrepaidItems(albumFolder);
};

const shouldShowPricingCategoryHierarchyGreenScreenModal = (
  albumFolder,
  isOwner,
) => {
  if (!isOwner) {
    return false;
  }

  if (albumFolder?.name === 'T&I Portraits') {
    return (
      albumFolder?.root?.green_screen_enabled &&
      albumFolder?.children?.album_folders?.records?.length > 0 &&
      !!albumFolder?.children?.album_folders?.records?.find(
        ({ individual_photos_upload_type: indvUploadType }) =>
          indvUploadType === null,
      )
    );
  }

  if (albumFolder?.name === 'School Portraits') {
    return (
      albumFolder?.root?.green_screen_enabled &&
      !albumFolder?.individual_photos_upload_type
    );
  }

  return false;
};

export {
  isAlbumFolder,
  folderHasAlbums,
  folderHasSubFolders,
  folderHasChildren,
  createAlbumFolderTree,
  albumFolderIsRoot,
  folderLabel,
  getAlbumFolderSubscription,
  folderHasPpaAlbums,
  getFolderAncestorHierarchy,
  folderHasChildrenByTotals,
  isAlbumFolderTransferable,
  albumFolderHasCoverPhotos,
  albumFolderHasCharity,
  albumFolderIsPrePaid,
  folderHasSubfoldersByTotals,
  isFolderWatermarked,
  albumFolderHasFreeTrial,
  getUploadUrl,
  getAlbumFolderChildren,
  getAlbumFolderChildAlbums,
  getAlbumFolderChildAlbumFolders,
  isAlbumFolderDiscoverable,
  getFolderTreeHierarchy,
  getAlbumFolderHierarchyString,
  albumFolderSubscriptionRequired,
  getAlbumFolderIdentities,
  getAlbumFolderTagLinkBySubtype,
  isAlbumFolderTagSelectionRequiredForCheckout,
  canSelectActivitities,
  getAlbumFolderIdentitiesFromRosterIdentities,
  getAlbumFolderPaymentDescriptor,
  getAlbumFolderDiscountDescriptor,
  getAlbumFolderPhotoDeliveryDescriptor,
  allowsPartialSmartTagCompletion,
  getAlbumFolderAssignedPrintPackages,
  getAlbumFolderUnassignedPrintPackages,
  albumFolderRequiresPhotoSearchOnboarding,
  requiresGenderForOnboarding,
  isPhotoFinderCreditsDeliveryEnabled,
  isPhotoFinderCreditsPPGDeliveryEnabled,
  isShutterbugDeliveryEnabled,
  isParentPayDeliveryEnabled,
  isPhotoDeliverySetup,
  albumFolderHasPendingLink,
  isAlbumFolderForInternalUse,
  isAlbumFolderPaidMatching,
  isAlbumFolderForWebGalleryOnly,
  getAlbumFolderTagBaseUrlByBasename,
  albumFolderCanSelectFreeWebGalleryOnly,
  albumFolderIsOnboardable,
  albumFolderHasSearchSubscriptionContent,
  albumFolderHasPhotoFinderCreditsContent,
  isAlbumFolderPhotoFinderEnabled,
  albumFolderAnyoneCanUploadEnabled,
  albumFolderHasSmartTagsAccess,
  canAccessRevenueTab,
  shouldDisplayPPAWarning,
  isOutdatedAlbumFolder,
  albumFolderHasPurchasableAddons,
  folderShouldShowTaxesSettings,
  albumFolderSupportsSimplifiedJoin,
  getAlbumFolderAlbumFlatList,
  isAthleticsAlbumFolder,
  isNumberedJerseysAlbumFolder,
  isAlbumFolderWithPrepaidItems,
  isPrepaidOnlyAlbumFolder,
  shouldShowPricingCategoryHierarchyGreenScreenModal,
};
